import { CONFIG } from "@component/Mortgage/Calculator/data";
import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { Config } from "@config";
import { faMoneyBill1 as fasMoneyBill1, faHouse as fasHouse } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { Button, FormCurrency, FormDecidable } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";

export interface StepFields {
	wantsNewHouse: boolean;
	bbClassification: boolean;
	loanAmount: number;
	realEstateValue: number;
	income: number;
	inKhr: boolean;
}

export class Step2 extends AbstractStep<StepFields> {
	protected renderContent(): ReactNode {
		return (
			<>
				<Card.Body className="d-flex flex-column">
					<FormDecidable
						label="Új lakást szeretnél vásárolni, vagy építkeznél?"
						name="wantsNewHouse"
						validate={this.validWantsNewHouse}
						required
					/>
					<FormDecidable
						label="Alacsony energiaigényű, energiahatékony ingatlant veszel, építesz?"
						name="bbClassification"
						infoTooltip="Legalább 25 százalékban megújuló energiaforrást használ a lakás. Emellett a ház primer energiaigénye nem haladhatja meg a 90 kWh/m2/év mértéket."
						validate={this.validBbClassification}
						required
					/>
					<FormCurrency
						label="Hitelösszeg"
						name="loanAmount"
						placeholder="Felvenni kívánt összeg"
						prepend={<FontAwesomeIcon icon={fasMoneyBill1} />}
						infoTooltip="Mekkora hitelt szeretnél felvenni?"
						validate={this.validLoanAmount}
						required
					/>
					<FormCurrency
						label="Ingatlan értéke"
						name="realEstateValue"
						placeholder="Ingatlan értéke"
						prepend={<FontAwesomeIcon icon={fasHouse} />}
						infoTooltip="A hitel fedezetéül szolgáló ingatlan piaci értéke."
						validate={this.validRealEstateValue}
						required
					/>
					<FormCurrency
						label="Család havi nettó jövedelme"
						name="income"
						placeholder="Család havi nettó jövedelme"
						prepend={<FontAwesomeIcon icon={fasMoneyBill1} />}
						infoTooltip="A háztartás több jövedelme is összeadható!"
						validate={this.validIncome}
						required
					/>
					<FormDecidable
						label="Szerepelsz-e a KHR-ben?"
						name="inKhr"
						validate={this.validateInKhr}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<Button type="submit" className="text-uppercase">
						Tovább
					</Button>
				</Card.Footer>
			</>
		);
	}

	@autobind
	private validateInKhr(inKhr: boolean): string {
		if (inKhr) {
			return `KHR-es ként nem vehetsz fel hitelt!`;
		}
	}

	@autobind
	private validWantsNewHouse(wantsNewHouse: boolean): string {
		if (!wantsNewHouse) {
			return "A zöld hitel kizárólag új lakásvásárlására, vagy építésre fordítható.";
		}
	}

	@autobind
	private validBbClassification(bbClassification: boolean): string {
		if (!bbClassification) {
			return "A zöld hitelt kizárólag alacsony energiaigényű lakások finanszírozására lehet fordítani.";
		}
	}
	@autobind
	private validIncome(income: number): string {
		if (isNaN(income)) {
			return "A jövedelem megadása kötelező!";
		}

		if (income < Config.getMinWage()) {
			return `A megadható minimális összjövedelem ${formatText(
				Config.getMinWage(),
				"ft",
			)}, amely megegyezik az aktuális minimálbérrel.`;
		}

		if (income > CONFIG.INCOME_MAX) {
			return `A megadható maximális jövedelem ${formatText(CONFIG.INCOME_MAX, "ft")}!`;
		}
	}

	@autobind
	private validLoanAmount(loanAmount: number, values: StepFields): string {
		if (isNaN(loanAmount)) {
			return "A hitelösszeg megadása kötelező!";
		}

		if (loanAmount < CONFIG.MIN_LOAN_AMOUNT) {
			return `A minimálisan felvehető hitelösszeg ${formatText(CONFIG.MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (loanAmount > CONFIG.MAX_LOAN_AMOUNT) {
			return `A maximálisan felvehető hitelösszeg ${formatText(CONFIG.MAX_LOAN_AMOUNT, "ft")}!`;
		}

		if (loanAmount > values.realEstateValue * 0.8) {
			return `A hitelösszeg nem lehet nagyobb az ingatlan értékének 80%-ánál (${formatText(
				values.realEstateValue * 0.8,
				"ft",
			)})!`;
		}
	}

	@autobind
	private validRealEstateValue(realEstateValue: number): string {
		if (realEstateValue < CONFIG.MIN_REAL_ESTATE_VALUE) {
			return `${"A vásárolni kívánt ingatlan minimális értéke"} ${formatText(
				CONFIG.MIN_REAL_ESTATE_VALUE,
				"ft",
			)}!`;
		}

		if (realEstateValue > CONFIG.MAX_REAL_ESTATE_VALUE) {
			return `${"A vásárolni kívánt ingatlan maximális értéke"} ${formatText(
				CONFIG.MAX_REAL_ESTATE_VALUE,
				"ft",
			)}!`;
		}
	}

	protected getComponentName(): string {
		return "Step2";
	}
}
